import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    numberOfCans: 2
  });
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  // Check if user is coming from password reset email
  useEffect(() => {
    const checkResetToken = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');

      if (type === 'recovery' && accessToken) {
        setShowResetPassword(true);
        setShowForgotPassword(false);
        setIsLogin(false);
      }
    };

    checkResetToken();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match!');
      return;
    }

    if (newPassword.length < 8) {
      setStatus('error');
      setMessage('Password must be at least 8 characters long!');
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw new Error(error.message);
      }

      setStatus('success');
      setMessage('Password updated successfully! Redirecting to login...');
      
      setTimeout(() => {
        setShowResetPassword(false);
        setIsLogin(true);
        setNewPassword('');
        setConfirmPassword('');
        setStatus('idle');
        setMessage('');
        window.history.replaceState({}, document.title, '/login');
      }, 2000);
    } catch (error: unknown) {
      setStatus('error');
      if (error instanceof Error) {
        setMessage(error.message || 'Failed to update password. Please try again.');
      } else {
        setMessage('Failed to update password. Please try again.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (error) {
          console.error('Login error:', error);
          // Provide user-friendly error messages
          if (error.message === 'Invalid login credentials') {
            throw new Error('Email or password is incorrect. Please check your credentials and try again.');
          } else if (error.message.includes('Email not confirmed')) {
            throw new Error('Please check your email and confirm your account before logging in.');
          } else {
            throw new Error(error.message || 'Unable to log in. Please try again.');
          }
        }

        if (!data.user) {
          throw new Error('Login failed. Please try again.');
        }
        
        setStatus('success');
        setMessage('Welcome back! Redirecting to your account...');
        setTimeout(() => window.REACT_APP_NAVIGATE('/account'), 1500);
      } else {
        // Sign up - Store profile data in localStorage temporarily
        const profileData = {
          fullName: formData.fullName,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          numberOfCans: formData.numberOfCans
        };
        
        // Save to localStorage for later use
        localStorage.setItem('pendingProfile', JSON.stringify(profileData));

        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              full_name: formData.fullName,
            },
            emailRedirectTo: window.location.origin + '/account'
          }
        });

        if (authError) {
          console.error('Signup error:', authError);
          // Provide user-friendly error messages for signup
          if (authError.message.includes('already registered')) {
            throw new Error('This email is already registered. Please log in instead.');
          } else {
            throw new Error(authError.message || 'Unable to create account. Please try again.');
          }
        }

        if (!authData.user) {
          throw new Error('Failed to create account. Please try again.');
        }

        // If user is immediately logged in (no email confirmation required)
        if (authData.session) {
          setStatus('success');
          setMessage('Account created! Setting up your profile...');
          // Redirect to account page where profile will be created
          setTimeout(() => window.REACT_APP_NAVIGATE('/account'), 2000);
        } else {
          // Email confirmation is required
          setStatus('success');
          setMessage('Account created! Please check your email to confirm your account. Your profile will be saved after confirmation.');
        }
      }
    } catch (error: unknown) {
      setStatus('error');
      if (error instanceof Error) {
        console.error('Auth error:', error);
        setMessage(error.message || 'Something went wrong. Please try again.');
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      // Get the current URL origin, but ensure it's not localhost in production
      const currentOrigin = window.location.origin;
      const redirectUrl = currentOrigin.includes('localhost') 
        ? currentOrigin + '/login'
        : currentOrigin + '/login';

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: redirectUrl,
      });

      if (error) {
        throw new Error(error.message);
      }

      setStatus('success');
      setMessage('Password reset email sent! Please check your inbox and click the link to reset your password.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setStatus('idle');
        setMessage('');
        setResetEmail('');
      }, 5000);
    } catch (error: unknown) {
      setStatus('error');
      if (error instanceof Error) {
        setMessage(error.message || 'Failed to send reset email. Please try again.');
      } else {
        setMessage('Failed to send reset email. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-yellow-50">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-16 px-4">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-8 shadow-2xl"
          >
            <div className="text-center mb-8">
              <h1 className="text-4xl font-black text-blue-600 mb-2" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                {showResetPassword ? 'Set New Password' : showForgotPassword ? 'Reset Password' : isLogin ? 'Welcome Back!' : 'Join Can-do Crew!'}
              </h1>
              <p className="text-gray-600">
                {showResetPassword 
                  ? 'Enter your new password below'
                  : showForgotPassword 
                  ? 'Enter your email to receive a password reset link' 
                  : isLogin ? 'Log in to manage your account' : 'Create your account today'}
              </p>
            </div>

            {showResetPassword ? (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New Password"
                  required
                  minLength={8}
                  className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                />

                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm New Password"
                  required
                  minLength={8}
                  className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                />

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-4 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                  style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
                >
                  {status === 'loading' ? 'Updating...' : 'Update Password'}
                </button>

                {status === 'success' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-green-100 text-green-800 px-4 py-3 rounded-2xl text-center font-bold text-sm"
                  >
                    {message}
                  </motion.div>
                )}

                {status === 'error' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-red-100 text-red-800 px-4 py-3 rounded-2xl text-center font-bold text-sm"
                  >
                    {message}
                  </motion.div>
                )}
              </form>
            ) : showForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Email Address"
                  required
                  className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                />

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-4 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                  style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
                >
                  {status === 'loading' ? 'Sending...' : 'Send Reset Link'}
                </button>

                {status === 'success' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-green-100 text-green-800 px-4 py-3 rounded-2xl text-center font-bold text-sm"
                  >
                    {message}
                  </motion.div>
                )}

                {status === 'error' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-red-100 text-red-800 px-4 py-3 rounded-2xl text-center font-bold text-sm"
                  >
                    {message}
                  </motion.div>
                )}

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setStatus('idle');
                      setMessage('');
                      setResetEmail('');
                    }}
                    className="text-blue-600 font-bold hover:underline cursor-pointer"
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="Full Name"
                        required
                        className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                      />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Phone Number"
                        required
                        className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                      />
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Street Address"
                        required
                        className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="City"
                          required
                          className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                        />
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          placeholder="State"
                          required
                          className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                        />
                      </div>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        placeholder="ZIP Code"
                        required
                        className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                      />
                      <select
                        name="numberOfCans"
                        value={formData.numberOfCans}
                        onChange={handleChange}
                        required
                        className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm cursor-pointer"
                      >
                        <option value="1">1 Trash Can</option>
                        <option value="2">2 Trash Cans</option>
                        <option value="3">3 Trash Cans</option>
                        <option value="4">4+ Trash Cans</option>
                      </select>
                    </>
                  )}

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    required
                    className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                  />

                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    required
                    minLength={6}
                    className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                  />

                  {isLogin && (
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-blue-600 hover:underline cursor-pointer"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-4 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                    style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
                  >
                    {status === 'loading' ? 'Please wait...' : isLogin ? 'Log In!' : 'Sign Up!'}
                  </button>

                  {status === 'success' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-green-100 text-green-800 px-4 py-3 rounded-2xl text-center font-bold text-sm"
                    >
                      {message}
                    </motion.div>
                  )}

                  {status === 'error' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-red-100 text-red-800 px-4 py-3 rounded-2xl text-center font-bold text-sm"
                    >
                      {message}
                    </motion.div>
                  )}
                </form>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      setIsLogin(!isLogin);
                      setStatus('idle');
                      setMessage('');
                    }}
                    className="text-blue-600 font-bold hover:underline cursor-pointer"
                  >
                    {isLogin ? "Don't have an account? Sign Up!" : 'Already have an account? Log In!'}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}